/* eslint-disable no-unused-expressions */
// ════════════════════════════════════════════════════════════════
// QuestionnaireScreen.jsx
// ACCESS baseline PROM questionnaire, rendered inside the GSA shell.
//
// Surfaces after the Recovery Plan (Group 3) for ACCESS users only.
// Fetches the problem-area instrument (ODI for LBP, KOOS JR for knee,
// HOOS JR for hip) from cloud_questionnaires/get-questionnaire and
// submits to cloud_questionnaires/submit-questionnaire using the
// existing payload format (answer text keyed by question_id). The
// backend looks up and stores answer weights itself; no scoring
// happens on the frontend.
//
// Design (agreed 2026-07-04):
// - Single scrolling page. Each question is a distinct card with
//   "Question n of N" numbering. Large tap targets for 65+ users.
// - Tapping an option selects it; tapping another moves the
//   selection. Nothing auto-advances, nothing locks until submit.
// - The button under the questions shows "n of N answered" while
//   incomplete. Tapping it scrolls to the first unanswered question
//   and highlights it; tapping again goes to the next one. When all
//   required questions are answered it becomes the Submit button.
// - Instrument fidelity: question and answer text render verbatim
//   from the backend. Do not rewrite, truncate, or reorder.
// ════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback } from "react";
import { MYRA_MESSAGES } from "../GSA_MYRA_MESSAGES";
import styles from "./QuestionnaireScreen.module.css";
import { GET_QUESTIONNAIRE_PUBLIC, SUBMIT_QUESTIONNAIRE, SaveQuestionnaireTarget } from "../GSA_CONSTANTS";
import MyraInteraction from "../MyraInteractionController";
import { AnalyticsService } from "../analytics/AnalyticsService";

// ── ACCESS instrument map ────────────────────────────────────
// TODO(ACCESS): fill in the real questionnaire ids once the five
// instruments are loaded in the questionnaire tables. A null
// questionnaireId means "no instrument for this area" and the
// PlanScreen gate will not route the user here.
// Consider relocating to GSA_CONSTANTS.js once ids are final.
export const ACCESS_QUESTIONNAIRE_BY_AREA = {
  LBP: { questionnaireId: 31, instrumentKey: "ODI" },
  NECK: { questionnaireId: 40, instrumentKey: "NDI" },
  SHOULDER: { questionnaireId: 41, instrumentKey: "QUICKDASH" }, // arm/hand/elbow/wrist too
  KNEE: { questionnaireId: 42, instrumentKey: "KOOS_JR" },
  HIP: { questionnaireId: 43, instrumentKey: "HOOS_JR" },
  THIGH: { questionnaireId: 44, instrumentKey: "PROMIS" },
  ANKLE: { questionnaireId: 44, instrumentKey: "PROMIS" },
  OTHER: { questionnaireId: 44, instrumentKey: "PROMIS" },
};

export function getAccessQuestionnaireConfig(problemArea) {
  const cfg = ACCESS_QUESTIONNAIRE_BY_AREA[problemArea];
  if (!cfg || !cfg.questionnaireId || !cfg.instrumentKey) return null;
  return cfg;
}
export async function replayPendingQuestionnaireSubmit() {
  const raw = localStorage.getItem("pendingQuestionnaireSubmit");
  if (!raw) return;

  const isAccessUser = window.is_access_user === true || localStorage.getItem("accessUser") === "1";
  if (!isAccessUser) {
    localStorage.removeItem("pendingQuestionnaireSubmit");
    return;
  }

  let pending;
  try {
    pending = JSON.parse(raw);
  } catch (e) {
    localStorage.removeItem("pendingQuestionnaireSubmit");
    return;
  }

  const userData = localStorage.getItem("userData");
  const token = userData ? JSON.parse(userData).token : "";
  if (!token) return; // not authenticated yet — leave pending, try again later

  try {
    const res = await fetch(SUBMIT_QUESTIONNAIRE, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token },
      body: JSON.stringify({
        answers: pending.answers,
        questionnaire_id: pending.questionnaire_id,
        patientAssesment: false,
        access_timepoint: "baseline",
        access_collection_date: pending.collectedAt || null,
      }),
    });
    const data = await res.json();

    if (data.responseCode !== 200) {
      console.error("[replayPendingQuestionnaireSubmit] submit-questionnaire failed:", data);
      return; // leave pending for a retry (e.g. on next screen mount)
    }

    localStorage.removeItem("pendingQuestionnaireSubmit");

    const sessionId = window.trSessionId || localStorage.getItem("trSessionId");
    if (sessionId) {
      const cfg = getAccessQuestionnaireConfig(pending.problemArea);
      fetch(SaveQuestionnaireTarget, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({
          sessionId,
          questionnaireId: pending.questionnaire_id,
          problemArea: pending.problemArea,
          instrumentKey: cfg?.instrumentKey,
          promScores: data.responseData?.access_prom_scores || {},
        }),
      }).catch((error) => {
        console.error("[replayPendingQuestionnaireSubmit] save-questionnaire-target failed (non-blocking):", error);
      });
    }
  } catch (error) {
    console.error("[replayPendingQuestionnaireSubmit] request failed:", error);
  }
}

// ── Myra copy fallbacks ──────────────────────────────────────
// Canonical copy lives in GSA_MYRA_MESSAGES.js under
// MYRA_MESSAGES.questionnaire (see delta doc). These fallbacks keep
// the screen functional if the messages file has not been updated.
export const QUESTIONNAIRE_FALLBACK_MSGS = {
  PLAN_BRIDGE: "One more short step before you wrap up. I have a standard set of questions about how your pain affects everyday activities. Your answers become the starting point we measure your recovery against.",
  INTRO: "Take your time with these. Tap the answer that fits best, and you can change any answer before you submit.",
  OUTRO: "Thank you, your answers are saved. Later in your recovery we will revisit these same questions so you can see your progress in real numbers.",
  FETCH_ERROR: "I could not load your questions just now. Please tap Try again in a moment.",
  SUBMIT_ERROR: "I could not save your answers just now. Your selections are still here, so please tap Submit again.",
  // {area} is replaced with the problem-area label (knee, thigh, hip, …)
  TOP_INSTRUCTIONS: "This survey asks for your view about your {area}. This information will help us keep track of how you feel about your {area} and how well you are able to do your usual activities. Answer every question by tapping the appropriate option, only one for each question. If you are unsure about how to answer a question, please give the best answer you can.",
};

function msg(key) {
  return MYRA_MESSAGES.questionnaire?.[key] || QUESTIONNAIRE_FALLBACK_MSGS[key];
}

// Normalize legacy question_type casing; "1" is the scored
// single-select used by every CMS instrument.
function normalizeType(t) {
  const s = String(t || "").toLowerCase();
  return s === "1" ? "radio" : s;
}

// ── Answer scale colour ──────────────────────────────────────
// Six stop green to red scale, declared on :root in
// SelfAssessment.module.css. Stops 1, 4 and 6 alias the existing
// --green, --amber and --red tokens. Five option instruments drop
// stop 3 (the light yellow).
const SCALE_STOPS_6 = ["var(--scale-1)", "var(--scale-2)", "var(--scale-3)", "var(--scale-4)", "var(--scale-5)", "var(--scale-6)"];
const SCALE_STOPS_5 = ["var(--scale-1)", "var(--scale-2)", "var(--scale-4)", "var(--scale-5)", "var(--scale-6)"];

function buildScale(count) {
  if (count <= 1) return [SCALE_STOPS_6[0]];
  if (count === 5) return SCALE_STOPS_5;
  if (count === 6) return SCALE_STOPS_6;
  return Array.from({ length: count }, (_, i) => SCALE_STOPS_6[Math.round((i * 5) / (count - 1))]);
}

// Colour is assigned by answer_weight rank, never by render position.
// Options still render in the order the backend sends them, verbatim.
// If any weight is missing or non numeric the whole set falls back to
// backend order so the ramp still runs best to worst.
function scaleByWeight(answers) {
  const stops = buildScale(answers.length);
  const rows = answers.map((a, i) => ({ i, w: Number(a.answer_weight) }));
  const usable = rows.every((r) => Number.isFinite(r.w));
  const ordered = usable ? [...rows].sort((a, b) => a.w - b.w) : rows;
  const out = {};
  ordered.forEach((row, rank) => {
    out[row.i] = stops[rank];
  });
  return out;
}

// A question drops to one option per row when its longest option is
// too long to sit beside another. 28 characters puts ODI and NDI on
// their own rows and leaves KOOS JR, HOOS JR, QuickDASH and PROMIS
// wrapping several per row.
const WIDE_OPTION_CHARS = 28;

function isWideOptionSet(answers) {
  return (answers || []).some((a) => String(a.name || "").length > WIDE_OPTION_CHARS);
}

// Subtitle-type questions cover two different things: short section
// labels ("Stiffness", "Pain") and longer instructional paragraphs
// that introduce the questions below them. Text at or under 40
// characters renders as a bold section heading; longer text renders
// as body copy. Threshold is a judgment call, not a spec value —
// revisit if a new instrument's short labels run long.
const STATIC_HEADING_CHARS = 40;

export default function QuestionnaireScreen({ assessmentData, submitQuestionnaireRef, showMyraMessage, scrollToBottom, onSubmitComplete, speakChatTextIfVoice }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [data, setData] = useState(null);
  // answers: { [question_id]: { answerId, name, weight } }
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const isVoiceMode = MyraInteraction.getMode() === "voice";

  const introShownRef = useRef(false);
  const jumpCountRef = useRef(0);
  const highlightTimerRef = useRef(null);
  const questionRefs = useRef({});

  const config = getAccessQuestionnaireConfig(assessmentData?.problemArea);

  // ── Screen tracking ────────────────────────────────────────
  useEffect(() => {
    window.screenTracker?.enterScreen("access_questionnaire");
    AnalyticsService.trackStep({
      is_access_user: true,
      access_questionnaire_viewed: true,
      screenFlow: window.screenTracker?.getScreenFlow?.() || [],
    });
    return () => {
      window.screenTracker?.exitScreen("access_questionnaire", { completed: submitted });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch questionnaire ────────────────────────────────────
  const fetchQuestionnaire = useCallback(() => {
    if (!config) {
      console.error("[QuestionnaireScreen] No ACCESS questionnaire configured for area:", assessmentData?.problemArea);
      setLoading(false);
      setLoadError(true);
      return;
    }
    setLoading(true);
    setLoadError(false);
    fetch(GET_QUESTIONNAIRE_PUBLIC, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ questionnaire_id: config.questionnaireId }),
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.responseData && res.responseData.sections) {
          setData(res.responseData);
          setLoading(false);
        } else {
          setLoading(false);
          setLoadError(true);
        }
      })
      .catch((error) => {
        console.error("[QuestionnaireScreen] get-questionnaire failed:", error);
        setLoading(false);
        setLoadError(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.questionnaireId]);

  useEffect(() => {
    fetchQuestionnaire();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Myra intro (once, after load succeeds) ─────────────────
  useEffect(() => {
    if (!data || introShownRef.current) return;
    introShownRef.current = true;
    (async () => {
      await showMyraMessage?.(msg("INTRO"), 400);
      if (isVoiceMode) {
        speakChatTextIfVoice?.(msg("INTRO"));
      }
      // setTimeout(() => scrollToBottom?.("smooth"), 150);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // ── Build the flat, numbered question list ─────────────────
  // Numbering covers answerable questions only (Subtitle rows render
  // as static text and are not counted). Required = not optional.
  const numbered = [];
  if (data?.sections) {
    for (const section of data.sections) {
      for (const q of section.questions || []) {
        if (normalizeType(q.question_type) === "subtitle") continue;
        numbered.push(q);
      }
    }
  }
  const totalCount = numbered.length;
  const requiredIds = numbered.filter((q) => !q.is_optional).map((q) => q.question_id);
  const answeredRequired = requiredIds.filter((id) => answers[id]).length;
  const unansweredIds = requiredIds.filter((id) => !answers[id]);
  const allAnswered = unansweredIds.length === 0;

  // ── Select / change an answer ──────────────────────────────
  const handleSelect = (question, answer) => {
    if (submitted || submitting) return;
    setSubmitError(false);
    window.screenTracker?.recordEvent?.("access_questionnaire", "answer_selected", {
      questionId: question.question_id,
      answerId: answer.id,
    });
    setAnswers((prev) => ({
      ...prev,
      [question.question_id]: {
        answerId: answer.id,
        name: answer.name,
        weight: answer.answer_weight,
      },
    }));
  };

  // ── Jump to next unanswered question ───────────────────────
  const jumpToUnanswered = () => {
    if (unansweredIds.length === 0) return;
    const idx = jumpCountRef.current % unansweredIds.length;
    jumpCountRef.current = jumpCountRef.current + 1;
    const targetId = unansweredIds[idx];
    const el = questionRefs.current[targetId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightId(targetId);
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => setHighlightId(null), 2000);
    }
  };

  useEffect(() => () => clearTimeout(highlightTimerRef.current), []);

  const saveQuestionnaireTarget = (promScores) => {
    const sessionId = assessmentData?.sessionId || window.screenTracker?.sessionId;
    if (!sessionId) {
      console.warn("[QuestionnaireScreen] No sessionId available; skipping target lock.");
      return;
    }
    const userData = localStorage.getItem("userData");
    fetch(SaveQuestionnaireTarget, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: userData ? JSON.parse(userData).token : "" },
      body: JSON.stringify({
        sessionId,
        questionnaireId: config.questionnaireId,
        problemArea: assessmentData?.problemArea,
        instrumentKey: config.instrumentKey,
        promScores: promScores || {},
      }),
    }).catch((error) => {
      console.error("[QuestionnaireScreen] save-questionnaire-target failed (non-blocking):", error);
    });
  };

  // ── Submit ─────────────────────────────────────────────────
  // Same contract the backend already handles: answer text keyed by
  // question_id. The backend looks up each answer's weight from the
  // text and stores it (QuestionnairesCloudService.submitQuestionnaire),
  // so the text must be sent verbatim.
  const handleSubmit = () => {
    if (!allAnswered || submitting || submitted) return;
    setSubmitting(true);
    setSubmitError(false);

    const payload = {};
    for (const q of numbered) {
      if (answers[q.question_id]) payload[q.question_id] = answers[q.question_id].name;
    }

    // Snapshot of question text + chosen answer text, keyed off the render-time
    // `data`/`answers` closure. Handed to onSubmitComplete so the caller can
    // freeze it into conversation history — the questionnaire UI itself
    // unmounts on completion, so this is the only surviving record.
    const submittedQuestionnaireData = {
      mainTitle: data?.main_title || null,
      sections: (data?.sections || []).map((section) => ({
        title: section.title || null,
        questions: (section.questions || [])
          .filter((q) => normalizeType(q.question_type) !== "subtitle")
          .map((q) => ({
            questionId: q.question_id,
            questionText: q.question,
            answerText: answers[q.question_id]?.name || null,
            options: q.answers || [],
            selectedAnswerId: answers[q.question_id]?.answerId ?? null,
          })),
      })),
    };
    (async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("userData") || "null");
        const res = await fetch(SUBMIT_QUESTIONNAIRE, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: userData?.token || "" },
          body: JSON.stringify({
            answers: payload,
            questionnaire_id: config.questionnaireId,
            patientAssesment: false,
            access_timepoint: "baseline",
            access_collection_date: new Date().toISOString(),
          }),
        });
        const data = await res.json();
        if (data.responseCode !== 200) {
          throw new Error(data?.responseMessage || "submit-questionnaire failed");
        }

        const sessionId = window.trSessionId || localStorage.getItem("trSessionId");
        if (sessionId) {
          fetch(SaveQuestionnaireTarget, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: userData?.token || "" },
            body: JSON.stringify({
              sessionId,
              questionnaireId: config.questionnaireId,
              problemArea: assessmentData?.problemArea,
              instrumentKey: config.instrumentKey,
              promScores: data.responseData?.access_prom_scores || {},
            }),
          }).catch((error) => console.error("[QuestionnaireScreen] save-questionnaire-target failed (non-blocking):", error));
        }

        setSubmitting(false);
        setSubmitted(true);
        window.screenTracker?.exitScreen("access_questionnaire", { completed: true });
        AnalyticsService.trackStep({
          is_access_user: true,
          access_questionnaire_completed: true,
          screenFlow: window.screenTracker?.getScreenFlow?.() || [],
        });

        await showMyraMessage?.(msg("OUTRO"), 400);
        if (isVoiceMode) speakChatTextIfVoice?.(msg("OUTRO"));
        setTimeout(() => scrollToBottom?.("smooth"), 150);
        onSubmitComplete?.(submittedQuestionnaireData);
      } catch (e) {
        console.error("[QuestionnaireScreen] submit-questionnaire failed:", e);
        setSubmitting(false);
        setSubmitError(true);
      }
    })();
  };

  if (submitQuestionnaireRef) submitQuestionnaireRef.current = { handleSubmit };

  // ── Render ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={styles.qWrap}>
        <div className={styles.qLoading}>
          <div className={styles.qSpinner} />
          <span>Loading your questions…</span>
        </div>
      </div>
    );
  }

  if (loadError || !data) {
    return (
      <div className={styles.qWrap}>
        <div className={styles.qErrorCard}>
          <p>{msg("FETCH_ERROR")}</p>
          <button className={styles.retryBtn} onClick={fetchQuestionnaire}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  let runningNumber = 0;
  // default top instructions incase of no top instructions returned
  const topInstructions = data.top_instructions || (assessmentData.problemArea ? msg("TOP_INSTRUCTIONS").replace(/\{area\}/g, AREA_LABELS[assessmentData?.problemArea]) : null);

  return (
    <div className={styles.qWrap}>
      {/* {data.main_title && <div className={styles.qTitle}>{data.main_title}</div>} */}
      {topInstructions && <div className={styles.qInstructions}>{topInstructions}</div>}

      {data.sections.map((section, sIdx) => (
        <React.Fragment key={sIdx}>
          {section.title && <div className={styles.qSectionHeader}>{section.title}</div>}

          {(section.questions || []).map((question) => {
            const type = normalizeType(question.question_type);

            // Static copy rows
            if (type === "subtitle") {
              const isHeading = String(question.question || "").length <= STATIC_HEADING_CHARS;
              return (
                <div key={question.question_id} className={isHeading ? styles.qStaticHeading : styles.qStaticText}>
                  {question.question}
                </div>
              );
            }

            if (type !== "radio") {
              // Unknown/legacy type: log and degrade to a radio list if
              // options exist, per the schema spec's registry guidance.
              console.warn("[QuestionnaireScreen] Unexpected question_type:", question.question_type, "for question", question.question_id);
            }

            runningNumber += 1;
            const selected = answers[question.question_id];
            const isHighlighted = highlightId === question.question_id;

            return (
              <div key={question.question_id} ref={(el) => (questionRefs.current[question.question_id] = el)} className={`${styles.qCard} ${isHighlighted ? styles.qCardMissing : ""}`}>
                <div className={styles.qNum}>
                  Question {runningNumber} of {totalCount}
                  {question.is_optional ? " (optional)" : ""}
                </div>
                <div className={styles.qText}>{question.question}</div>
                {question.question_subtitle && <div className={styles.qSub}>{question.question_subtitle}</div>}

                {(() => {
                  const opts = question.answers || [];
                  const wide = isWideOptionSet(opts);
                  const scale = scaleByWeight(opts);
                  return (
                    <div role="radiogroup" aria-label={question.question} className={`${styles.qOptions} ${wide ? styles.qOptionsWide : ""}`}>
                      {opts.map((answer, idx) => {
                        const isSelected = selected && selected.answerId === answer.id;
                        return (
                          <button key={answer.id} type="button" role="radio" aria-checked={!!isSelected} className={`${styles.optBtn} ${isSelected ? styles.optSelected : ""} ${submitted ? styles.optLocked : ""}`} style={isSelected ? undefined : { borderColor: scale[idx] }} onClick={() => handleSelect(question, answer)}>
                            <span className={styles.optRadio} aria-hidden="true">
                              <span className={styles.optRadioDot} />
                            </span>
                            <span className={styles.optLabel}>{answer.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </React.Fragment>
      ))}

      {data.bottom_instructions && <div className={styles.qInstructions}>{data.bottom_instructions}</div>}

      {submitError && <div className={styles.qErrorBanner}>{msg("SUBMIT_ERROR")}</div>}

      {!submitted ? (
        allAnswered ? (
          <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Saving…" : "Submit my answers"}
          </button>
        ) : (
          <>
            <button className={`${styles.submitBtn} ${styles.submitIncomplete}`} onClick={jumpToUnanswered}>
              {answeredRequired} of {requiredIds.length} answered
            </button>
            <div className={styles.submitHint}>Tap the button above to go to your next unanswered question.</div>
          </>
        )
      ) : (
        <div className={styles.savedBadge}>
          <span className={styles.savedCheck} aria-hidden="true">
            ✓
          </span>
          Answers saved
        </div>
      )}
    </div>
  );
}

// consent screen for access users
const AREA_LABELS = { LBP: "lower back", HIP: "hip", THIGH: "thigh", KNEE: "knee" };

const CONSENT_BRIDGE_TEXT = "One last step before your plan is ready. Medicare requires us to walk through a few things and record that you agreed, since Virdio Health's program is part of a federal payment model.";
const ACCESS_ALIGNMENT_CONSENT_VERSION = "access-alignment-consent-2026-08-26-v1";

function buildAlignmentConsentEvidence(areaLabel, decision) {
  return {
    decision,
    consentedAt: new Date().toISOString(),
    wordingVersion: ACCESS_ALIGNMENT_CONSENT_VERSION,
    costSharingAmount: 0,
    source: "web-self-assessment",
    wordingSnapshot: {
      heading: "Ready to enroll?",
      framing: "The services Virdio Health offers are part of a new federal payment model test run by the Centers for Medicare and Medicaid Services, called the Advancing Chronic Care with Effective, Scalable Solutions (ACCESS) Model. By enrolling with Virdio Health to receive ACCESS services, you are agreeing to the following.",
      items: [
        { title: "Enrolling is your choice", text: "You do not have to enroll. Your Medicare benefits will not be affected by your decision either way." },
        { title: "What this costs you", amount: "$0.00", text: "per month, for as long as you stay enrolled with Virdio Health. Medicare pays Virdio Health directly, and Virdio Health covers the share you would normally owe. There is no copay and no deductible for this program." },
        { title: "You can leave", text: "Beginning 90 days after you enroll, you can end your enrollment with Virdio Health at any time. If you want to, you can then enroll with a different participating provider." },
        { title: "One provider for each type of care", text: `You can be enrolled with only one participating provider for each ACCESS clinical track at a time. This one covers your ${areaLabel}. If you have a qualifying condition in another track as well, you can enroll in that one too — with Virdio Health or with a different provider.` },
        { title: "Your Medicare claims", text: "Your Medicare claims data may be shared with Virdio Health. Federal privacy and security protections apply, including HIPAA." },
      ],
      acknowledgement: `I have read the information above and I am choosing to enroll with Virdio Health for my ACCESS ${areaLabel} care.`,
      acknowledgementDetail: "This is your choice. You do not have to enroll, and your Medicare benefits are not affected either way. You can leave at any time after 90 days.",
    },
  };
}

export function ConsentScreen({ assessmentData, submitConsentRef, showMyraMessage, scrollToBottom, onConsentComplete, speakChatTextIfVoice }) {
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState(false);
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);
  const lockedRef = useRef(false);
  const isVoiceMode = MyraInteraction.getMode() === "voice";

  const areaLabel = AREA_LABELS[assessmentData?.problemArea] || "condition";
  const errorText = "Please tick the box above so we can record that you agreed to enroll";

  useEffect(() => {
    window.screenTracker?.enterScreen("access_alignment_consent");
    AnalyticsService.trackStep({
      is_access_user: true,
      access_alignment_consent_viewed: true,
      screenFlow: window.screenTracker?.getScreenFlow?.() || [],
    });
    let cancelled = false;
    async function init() {
      await showMyraMessage?.(CONSENT_BRIDGE_TEXT, 400);
      if (isVoiceMode) {
        speakChatTextIfVoice?.(CONSENT_BRIDGE_TEXT);
      }
      if (!cancelled) {
        setReady(true);
        setTimeout(() => scrollToBottom?.("smooth"), 150);
      }
    }
    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = () => {
    if (lockedRef.current) return;
    setChecked((prev) => {
      const next = !prev;
      window.screenTracker?.recordEvent?.("access_alignment_consent", "acknowledgement_toggled", next);
      return next;
    });
    setError(false);
  };

  const finish = useCallback(
    (consentGranted) => {
      if (lockedRef.current) return;
      const evidence = buildAlignmentConsentEvidence(areaLabel, consentGranted ? "granted" : "declined");
      localStorage.setItem("virdio_access_alignment_consent", JSON.stringify(evidence));
      window.screenTracker?.recordEvent?.("access_alignment_consent", "consent_decision", consentGranted ? "granted" : "declined");
      window.screenTracker?.exitScreen("access_alignment_consent", { completed: true, exitReason: consentGranted ? "granted" : "declined" });
      AnalyticsService.trackStep({
        is_access_user: true,
        screenFlow: window.screenTracker?.getScreenFlow?.() || [],
      });
      lockedRef.current = true;
      setLocked(true);
      onConsentComplete?.(consentGranted);
    },
    [areaLabel, onConsentComplete],
  );

  const handleSubmit = () => {
    if (!checked) {
      setError(true);
      if (isVoiceMode) {
        speakChatTextIfVoice?.(errorText);
      }
      return;
    }
    finish(true);
  };

  const handleNotNow = () => {
    finish(false);
  };

  if (submitConsentRef) submitConsentRef.current = { handleSubmit, handleNotNow };

  if (!ready) return null;

  return (
    <div className={styles.consentWrap}>
      <h1 className={styles.consentH1}>Ready to enroll?</h1>
      <div className={styles.consentWhy}>You have your plan. This is the last step — Medicare requires us to tell you these things before you enroll, and to record that you agreed.</div>

      <div className={styles.consentBlock}>
        <h2 className={styles.consentBlockTitle}>Before you enroll</h2>
        <p className={styles.consentFraming}>The services Virdio Health offers are part of a new federal payment model test run by the Centers for Medicare and Medicaid Services, called the Advancing Chronic Care with Effective, Scalable Solutions (ACCESS) Model. By enrolling with Virdio Health to receive ACCESS services, you are agreeing to the following.</p>

        <ol className={styles.consentItems}>
          <li>
            <h3>Enrolling is your choice</h3>
            <p>You do not have to enroll. Your Medicare benefits will not be affected by your decision either way.</p>
          </li>

          <li>
            <h3>What this costs you</h3>
            <div className={styles.consentCost}>
              <span className={styles.consentCostAmt}>$0.00</span>
              <span className={styles.consentCostPer}>per month, for as long as you stay enrolled with Virdio Health</span>
            </div>
            <p>Medicare pays Virdio Health directly, and Virdio Health covers the share you would normally owe. There is no copay and no deductible for this program.</p>
          </li>

          <li>
            <h3>You can leave</h3>
            <p>Beginning 90 days after you enroll, you can end your enrollment with Virdio Health at any time. If you want to, you can then enroll with a different participating provider.</p>
          </li>

          <li>
            <h3>One provider for each type of care</h3>
            <p>
              You can be enrolled with only one participating provider for each ACCESS clinical track at a time. This one covers your <strong>{areaLabel}</strong>.
            </p>
            <p>If you have a qualifying condition in another track as well, you can enroll in that one too — with Virdio Health or with a different provider.</p>
          </li>

          <li>
            <h3>Your Medicare claims</h3>
            <p>Your Medicare claims data may be shared with Virdio Health. Federal privacy and security protections apply, including HIPAA.</p>
          </li>
        </ol>
      </div>

      <label className={`${styles.consentRow} ${checked ? styles.consentRowOn : ""}`}>
        <input type="checkbox" checked={checked} onChange={handleToggle} disabled={locked} />
        <span className={styles.consentCtext}>
          I have read the information above and I am choosing to enroll with Virdio Health for my ACCESS <strong>{areaLabel}</strong> care.
          <span className={styles.consentCsub}>This is your choice. You do not have to enroll, and your Medicare benefits are not affected either way. You can leave at any time after 90 days.</span>
        </span>
      </label>

      {error && <div className={`${styles.consentErrMsg} ${styles.consentErrMsgOn}`}>{errorText}</div>}

      {!locked && (
        <div className={styles.consentActions}>
          <button type="button" className={`${styles.consentBtn} ${styles.consentBtnPrimary}`} onClick={handleSubmit}>
            Enroll and start my plan
          </button>
          <button type="button" className={`${styles.consentBtn} ${styles.consentBtnGhost}`} onClick={handleNotNow}>
            Not right now
          </button>
        </div>
      )}

      <div className={styles.consentHelpline}>
        Questions about your Medicare coverage or your rights? Call{" "}
        <a href="tel:18006334227">
          <strong>1-800-MEDICARE</strong>
        </a>{" "}
        (1-800-633-4227). TTY users call <strong>1-877-486-2048</strong>.
      </div>
    </div>
  );
}
